/* eslint-disable no-console */
import { getApxMonto } from "./apx";
import { calcularImporteNetoIntermediario } from "./calcularComisionIntermediario";
import { calcularComisionEdificiosTotal, esEdificioComisionFija } from "./comisionEdificiosUtil";
import { cargarSnapshotComision, cargarSnapshotPctCongelado } from "./comisionSnapshot";
import { cargarCostosIntermediacion } from "./costosIntermediacion";
import { loadExchangeRates, getDolarValue } from "./exchangeRateService";
import { explodarContratosComoFacturas } from "./explodarContratosComoFacturas";
import { getClientes } from "./getClientes";
import { getContratos } from "./getContratos";
import { getFacturasComisionTodos } from "./getFacturasComisionTodos";
import { getIntermediarios } from "./getIntermediarios";
import { getOportunidadesBTL } from "./getOportunidadesBTL";

const MESES_NOMBRE = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

/**
 * Último día de un mes dado (YYYY-MM-DD)
 */
const ultimoDiaMes = (year, month) => {
  const d = new Date(year, month, 0);
  return `${year}-${String(month).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Trimestre de un mes (1-12) → "Q1-YYYY"
 */
const getTrimestreLabel = (year, month) => {
  const q = Math.ceil(month / 3);
  return `${year} Q${q}`;
};

/**
 * Calcula el ledger de comisiones de edificios (débitos mensuales).
 *
 * Reutiliza 100% la lógica de CalculadorComisionEdificio y ER:
 * - calcularComisionEdificiosTotal (PAUTA + BTL)
 * - calcularImporteNetoIntermediario (descuento agencia)
 * - explodarContratosComoFacturas (contratos → facturas)
 *
 * @param {Object} params
 * @param {number[]} params.edificioIds - IDs de edificios a calcular
 * @param {Object} params.edificiosMap - { [id]: edificioCompleto }
 * @param {string} params.fechaInicio - "YYYY-MM-DD"
 * @param {string} params.fechaFin - "YYYY-MM-DD"
 * @param {string} params.pais - "Chile" | "Perú"
 * @returns {Promise<Array>} Entries mensuales de comisión
 */
export const calcularLedgerComisionEdificio = async ({ edificioIds, edificiosMap, fechaInicio, fechaFin, pais }) => {
  // Cargar tasas de cambio históricas (idempotente)
  await loadExchangeRates();

  // Snapshot de comisión (Paso 5): denominador congelado por venta/mes. El chokepoint prorratea con
  // este denominador en vez de `pantallastotales`. Si falla → null → fallback a pantallastotales.
  const snapMap = await cargarSnapshotComision(pais).catch(() => null);

  // A3 (7.22): % de intermediación congelado (foto). Vacío/null → todo en vivo.
  const pctMap = await cargarSnapshotPctCongelado(pais).catch(() => null);

  // FASE 6: take programático por venta (USD). El chokepoint trabaja en USD nativo y convierte a
  // local DESPUÉS, así que el take (USD) se pasa al motor de edificios tal cual (sin getTakeLocal).
  const costosMap = await cargarCostosIntermediacion(pais).catch(() => null);

  // 1. Fetch data (mismas fuentes que CalculadorComisionEdificio)
  // Parseo manual para evitar bug timezone UTC vs local
  const startYear = Number(fechaInicio.split("-")[0]);
  const endYear = Number(fechaFin.split("-")[0]);
  const years = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);

  const [clientesData, intermediariosData, contratosData, ...btlResults] = await Promise.all([
    getClientes({ pais }).catch(() => []),
    getIntermediarios(pais).catch(() => []),
    getContratos({ pais }).catch(() => []),
    ...years.map((ano) => getOportunidadesBTL({ pais, ano }).catch(() => [])),
  ]);

  // Normalizar tipos (misma lógica que CalculadorComisionEdificio)
  const clientes = (clientesData || []).map((c) => ({
    ...c,
    id: Number(c.id),
    agencia: c.agencia ? String(c.agencia).trim() : null,
  }));
  const intermediarios = (intermediariosData || []).map((i) => ({ ...i, id: Number(i.id) }));

  // 2. Fetch facturas PAUTA
  let facturasPauta = [];
  try {
    facturasPauta = await getFacturasComisionTodos({
      edificios: edificioIds,
      fechas: [fechaInicio, fechaFin],
      pais,
    });
    facturasPauta = facturasPauta.map((f) => ({ ...f, isBTL: false }));
  } catch {
    facturasPauta = [];
  }

  // 3. Contratos → facturas
  const facturasContratos = explodarContratosComoFacturas(contratosData || [], edificiosMap, fechaInicio, fechaFin);

  // 4. Unir PAUTA + Contratos
  const todasFacturas = [...facturasPauta, ...facturasContratos];

  // 5. Procesar BTL (misma lógica que CalculadorComisionEdificio líneas 232-304)
  const btlData = btlResults.flat();
  const fechaInicioDate = new Date(fechaInicio);
  const fechaFinDate = new Date(fechaFin);
  const edificioIdSet = new Set(edificioIds);

  const btlFiltradas = btlData.filter((sale) => {
    if (sale.pais !== pais || sale.eliminada) return false;
    const fecha = new Date(sale.facturafecha);
    if (isNaN(fecha.getTime())) return false;
    if (fecha < fechaInicioDate || fecha > fechaFinDate) return false;
    const buildings =
      sale.buildings && typeof sale.buildings === "string" ? JSON.parse(sale.buildings) : sale.buildings || [];
    return buildings.some((b) => edificioIdSet.has(b.id));
  });

  // Deduplicar BTL por id
  const uniqueBTL = new Map();
  btlFiltradas.forEach((sale) => {
    if (!uniqueBTL.has(sale.id)) {
      const buildings =
        sale.buildings && typeof sale.buildings === "string" ? JSON.parse(sale.buildings) : sale.buildings || [];
      uniqueBTL.set(sale.id, { ...sale, buildings, isBTL: true });
    }
  });

  // 6. Calcular comisiones por edificio por mes
  const resultado = [];

  // Iterar por cada mes en el rango (parseo manual para evitar bug timezone)
  const [sY, sM] = fechaInicio.split("-").map(Number);
  const [eY, eM] = fechaFin.split("-").map(Number);
  let loopY = sY,
    loopM = sM;

  while (loopY < eY || (loopY === eY && loopM <= eM)) {
    const year = loopY;
    const month = loopM;
    const mesKey = `${year}-${String(month).padStart(2, "0")}`;

    // Filtrar facturas de este mes
    const facturasMes = todasFacturas.filter((f) => {
      if (!f.facturafecha) return false;
      return f.facturafecha.startsWith(mesKey);
    });

    // Filtrar BTL de este mes
    const btlMes = [...uniqueBTL.values()].filter((s) => s.facturafecha && s.facturafecha.startsWith(mesKey));

    for (const edificioId of edificioIds) {
      const edificio = edificiosMap[edificioId];
      if (!edificio) continue;

      let comisionMes = 0;

      // PAUTA + Contratos: facturas donde participa este edificio
      facturasMes.forEach((factura) => {
        const participantes = factura.edificiosparticipantes
          ? factura.edificiosparticipantes.split(",").map((s) => Number(s.trim()))
          : [];
        if (!participantes.includes(edificioId)) return;

        const netoFactura = calcularImporteNetoIntermediario(factura, clientes, intermediarios, pais, pctMap);
        // FASE 6: take en USD nativo (se convierte a local junto con la comisión, más abajo).
        const takeUsd = factura.isContrato ? 0 : costosMap?.get(`sales:${factura.id}`) || 0;
        // APX aplica a sales Y contratos (apx_id a nivel contrato; gating por apx_id → 0 si no hay).
        const apxUsd = getApxMonto(factura, intermediarios, pctMap);
        const edificiosMapTemp = { [edificioId]: edificio };
        let comision = calcularComisionEdificiosTotal(factura, edificiosMapTemp, netoFactura, snapMap, takeUsd, apxUsd);

        // Convertir USD → moneda local con tasa histórica por fecha de factura
        if (factura.moneda_dolar) {
          const rate = getDolarValue(factura.facturafecha || factura.fecha_cierre, pais);
          comision = comision * rate;
        }

        comisionMes += comision;
      });

      // BTL: calcular comisión por building de este edificio
      btlMes.forEach((sale) => {
        const building = sale.buildings.find((b) => b.id === edificioId);
        if (!building) return;

        const edificiosMapTemp = { [edificioId]: edificio };
        // BTL no prorratea por pantallas → snapMap no aplica, se pasa por consistencia de firma.
        let comision = calcularComisionEdificiosTotal(sale, edificiosMapTemp, building.importe, snapMap);

        if (sale.moneda_dolar) {
          const rate = getDolarValue(sale.facturafecha || sale.fecha_cierre, pais);
          comision = comision * rate;
        }

        comisionMes += comision;
      });

      if (comisionMes > 0) {
        resultado.push({
          fecha: ultimoDiaMes(year, month),
          mes: mesKey,
          trimestre: getTrimestreLabel(year, month),
          edificioId,
          edificioNombre: edificio.edificio || `Edificio ${edificioId}`,
          concepto: `${MESES_NOMBRE[month - 1]} ${year}`,
          debito: Math.round(comisionMes),
          credito: 0,
          moneda: pais === "Chile" ? "CLP" : "PEN",
          tipo: "variable",
        });
      }

      // Comisión fija: independiente de facturas, se genera si el edificio tiene monto fijo activo
      const fechaPrimeroMes = `${year}-${String(month).padStart(2, "0")}-01`;
      if (esEdificioComisionFija(edificio, fechaPrimeroMes)) {
        const montoUSD = Number(edificio.comision_fija_monto) || 0;
        if (montoUSD > 0) {
          const monedaFija = (edificio.comision_fija_moneda || "USD").toUpperCase();
          const comisionFija = monedaFija === "USD" ? montoUSD * getDolarValue(fechaPrimeroMes, pais) : montoUSD;
          resultado.push({
            fecha: ultimoDiaMes(year, month),
            mes: mesKey,
            trimestre: getTrimestreLabel(year, month),
            edificioId,
            edificioNombre: edificio.edificio || `Edificio ${edificioId}`,
            concepto: `${MESES_NOMBRE[month - 1]} ${year} (fijo)`,
            debito: Math.round(comisionFija),
            credito: 0,
            moneda: pais === "Chile" ? "CLP" : "PEN",
            tipo: "fijo",
          });
        }
      }
    }

    // Avanzar al siguiente mes
    loopM++;
    if (loopM > 12) {
      loopM = 1;
      loopY++;
    }
  }

  return resultado;
};
